import axios from "axios";
import { getToken, clearAuth } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) { clearAuth(); window.location.href = "/login"; }
    return Promise.reject(err);
  }
);

export const loginApi = (username, password) =>
  api.post("/auth/login", { username, password }).then((r) => r.data);
export const changePasswordApi = (target_username, new_password) =>
  api.post("/auth/change-password", { target_username, new_password }).then((r) => r.data);
export const getMeta      = () => api.get("/meta").then((r) => r.data);
export const getToday     = () => api.get("/records/today").then((r) => r.data);
export const createRecord = (type, payload) => api.post(`/records/${type}`, payload).then((r) => r.data);
export const listRecords  = (type, params = {}) => api.get(`/records/${type}`, { params }).then((r) => r.data);
export const updateRecord = (type, id, payload) => api.put(`/records/${type}/${id}`, payload).then((r) => r.data);
export const deleteRecord = (type, id) => api.delete(`/records/${type}/${id}`).then((r) => r.data);
export const setSheetActive = (id, active) => api.put(`/production/sheets/${id}/active`, { active }).then((r) => r.data);
export const uploadFile   = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Ingredients
export const listIngredients  = () => api.get("/ingredients").then((r) => r.data);
export const createIngredient = (p) => api.post("/ingredients", p).then((r) => r.data);
export const updateIngredient = (id, p) => api.put(`/ingredients/${id}`, p).then((r) => r.data);
export const deleteIngredient = (id) => api.delete(`/ingredients/${id}`).then((r) => r.data);

// Fitxes de producció
export const listSheets    = () => api.get("/production/sheets").then((r) => r.data);
export const getSheet      = (id) => api.get(`/production/sheets/${id}`).then((r) => r.data);
export const createSheet   = (p) => api.post("/production/sheets", p).then((r) => r.data);
export const updateSheet   = (id, p) => api.put(`/production/sheets/${id}`, p).then((r) => r.data);
export const deleteSheet   = (id) => api.delete(`/production/sheets/${id}`).then((r) => r.data);
export const listSheetLogs = (id) => api.get(`/production/sheets/${id}/logs`).then((r) => r.data);
export const createSheetLog = (id, p) => api.post(`/production/sheets/${id}/logs`, p).then((r) => r.data);

// Matriu d'al·lèrgens (derivada)
export const getAllergenMatrix = () => api.get("/allergens/matrix").then((r) => r.data);

// Albarans — extracció IA
export const extractAlbara = (url) => api.post("/albarans/extract", { url }).then((r) => r.data);
export const extractAlbaraLines = (url, provider_id) =>
  api.post("/albarans/extract-lines", { url, provider_id }).then((r) => r.data);

// Proveïdors
export const listProviders  = () => api.get("/providers").then((r) => r.data);
export const createProvider = (p) => api.post("/providers", p).then((r) => r.data);
export const updateProvider = (id, p) => api.put(`/providers/${id}`, p).then((r) => r.data);
export const deleteProvider = (id) => api.delete(`/providers/${id}`).then((r) => r.data);

// Plantilles d'albarà
export const listAlbaranTemplates  = () => api.get("/albaran-templates").then((r) => r.data);
export const createAlbaranTemplate = (p) => api.post("/albaran-templates", p).then((r) => r.data);
export const updateAlbaranTemplate = (id, p) => api.put(`/albaran-templates/${id}`, p).then((r) => r.data);
export const deleteAlbaranTemplate = (id) => api.delete(`/albaran-templates/${id}`).then((r) => r.data);
export const analyzeAlbaran = (url) => api.post("/albaran-templates/analyze", { url }).then((r) => r.data);

// Productes de neteja
export const listCleaningProducts  = () => api.get("/cleaning-products").then((r) => r.data);
export const createCleaningProduct = (p) => api.post("/cleaning-products", p).then((r) => r.data);
export const updateCleaningProduct = (id, p) => api.put(`/cleaning-products/${id}`, p).then((r) => r.data);
export const deleteCleaningProduct = (id) => api.delete(`/cleaning-products/${id}`).then((r) => r.data);

// Trazabilitat — lots d'entrada disponibles per a una fitxa
export const getAvailableInputs = (sheetId) =>
  api.get(`/production/sheets/${sheetId}/available-inputs`).then((r) => r.data);
export const searchReceptionLots = (q) =>
  api.get("/reception/lots", { params: { q } }).then((r) => r.data);

// Elaboracions — editar (admin) / esborrar (admin o propietari del mateix dia)
export const updateSheetLog = (sheetId, logId, p) =>
  api.put(`/production/sheets/${sheetId}/logs/${logId}`, p).then((r) => r.data);
export const deleteSheetLog = (sheetId, logId) =>
  api.delete(`/production/sheets/${sheetId}/logs/${logId}`).then((r) => r.data);

// Plans APPCC
export const listPlans  = () => api.get("/appcc-plans").then((r) => r.data);
export const getPlan    = (number) => api.get(`/appcc-plans/${number}`).then((r) => r.data);
export const updatePlan = (number, p) => api.put(`/appcc-plans/${number}`, p).then((r) => r.data);

// PDF de fitxes (descàrrega autenticada via blob)
const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};
export const downloadSheetPdf = async (sheetId, name) => {
  const r = await api.get(`/production/sheets/${sheetId}/pdf`, { responseType: "blob" });
  const safe = (name || "fitxa").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
  downloadBlob(r.data, `fitxa-${safe}.pdf`);
};
export const downloadAllSheetsPdf = async () => {
  const r = await api.get("/production/sheets/pdf/all", { responseType: "blob" });
  downloadBlob(r.data, "fitxes-produccio-sublims.pdf");
};
